import { Component, OnInit, ViewContainerRef } from '@angular/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { DataSourceRequestState } from '@progress/kendo-data-query';
import { MaterialItemService } from '@fs/services/material-item';
import { TabOpenService } from '@services/tab-open.service';
import { PermissionPipe } from '@shared/pipes/permission';
import { Tabs } from '@enums/tab.enum';
import { ActionName } from '@shared/enums/action-name';
import { MaterialItemModel, Attachment, Item } from '@fs/models/price-book/material-item.model';
import { Constants } from '@shared/models/constants';
import { CommonMethods } from '@services/common.method';
import { AuthService } from '@services/auth.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { ProductItemType } from '@fs/enums/common-enums';

@Component({
  selector: 'app-material-item-detail',
  templateUrl: './material-detail.component.html',
  styleUrls: ['./material-detail.component.scss'],
  providers: [MaterialItemService]
})
export class MaterialItemDetailComponent implements OnInit {
  materialId: string;
  actionName = ActionName;
  details: MaterialItemModel;
  attachmentList: Attachment[] = [];
  attachments: GridDataResult = { data: [], total: 0 };
  pageSizes = { pageSizes: [50, 100, 300], refresh: true };
  defaultImage: string = Constants.DefaultImagePaths.Material;
  inputParams: any;
  state: DataSourceRequestState = {
    skip: 0,
    take: 50,
    sort: [{
      field: 'Description',
      dir: 'asc'
    }],
  };
  tabCounter: number;
  /** used for attachment slider */
  showAttachmentSlider: boolean = false;
  /** used for attachment slider */
  slideIndex: number = 0;
  qbVersion: string = "";
  productItemType = ProductItemType;

  constructor(
    private materialService: MaterialItemService,
    private permissionPipe: PermissionPipe,
    private tabopen: TabOpenService,
    private commonMethods: CommonMethods,
    private auth: AuthService,
    private showspinner: SpinnerService,
    private viewContainerRef: ViewContainerRef
  ) {
      this.qbVersion = (this.auth.getCompanyQBType()).toLowerCase();
  }

  //#region Life Cycle Events
  ngOnInit() {
    this.getMaterialItem();
  }
  //#endregion

  //#region Public Methods
  refreshTab() {
    this.getMaterialItem(Constants.defaultSpinnertimeout);
  }
  openEditMaterialItem() {
    if (!this.permissionPipe.transform(ActionName.EditItem)) {
      return
    }
    this.tabopen.ReplaceTab(
      {
        CurrentTabIndex: this.tabCounter,
        newTabInfo: {
          tabId: Tabs.FsLiteMaterialItemEdit,
          params: { ID: this.inputParams.ID },
          tabTitle: 'Material - ' + this.details.ItemNumber
        }
      })
  }
  /** used for attachment slider */
  viewAttachmentSlide(idx: number) {
    this.slideIndex = idx;
    this.showAttachmentSlider = true;
  }
  /** used for attachment slider */
  closeAttachmentSlider() {
    this.showAttachmentSlider = false;
  }
  //#endregion

  //#region Private Methods
  private getMaterialItem(timeout: number = 0) {
    this.showspinner.showloader(this.viewContainerRef, this.tabCounter, timeout);
    this.materialService.GetMaterialItem(this.inputParams.ID).subscribe(response => {
      if (response != null) {
        this.details = new MaterialItemModel();
        this.details.MaterialDetails = new Item;
        this.details.ItemNumber = response.ItemNumber;
        this.details.AccountingCode = response.AccountingCode;
        this.details.IsFrequentlyUsed = response.IsFrequentlyUsed;
        this.details.Description = response.Description;
        this.details.Note = response.Note;
        this.details.IsSellable = response.IsSellable;

        this.details.IsPriceEditAllowed = response.IsPriceEditAllowed;
        this.details.Prices = response.Prices;
        this.details.Category = response.Category;
        this.details.AccountingCode = response.AccountingCode;
        this.details.IsDescriptionEditAllowed = response.IsDescriptionEditAllowed;
        this.details.IsDiscountExcluded = response.IsDiscountExcluded;
        this.details.IsTaxable = response.IsTaxable;
        this.details.IsAgreementExcluded = response.IsAgreementExcluded;
        var materialDetails = response.MaterialDetails;

        this.details.MaterialDetails.BaseUOM = materialDetails.BaseUOM;
        this.details.MaterialDetails.StandardCost = materialDetails.StandardCost;
        this.details.MaterialDetails.DefaultReplenishmentMethod = materialDetails.DefaultReplenishmentMethod;
        this.details.MaterialDetails.LeadTime = materialDetails.LeadTime;
        this.details.MaterialDetails.Manufacturer = materialDetails.Manufacturer;
        this.details.MaterialDetails.ManufacturerPartNumber = materialDetails.ManufacturerPartNumber;
        this.details.MaterialDetails.IsReturnable = materialDetails.IsReturnable;
        this.details.MaterialDetails.BlockedFromReordering = materialDetails.BlockedFromReordering;

        this.attachments.data = response.Attachments;
        this.attachments.data.forEach(x => {
          x.IsImage = !this.checkIsNotImage(x);
          x.UploadedBy = x.CreatedByUser != undefined && x.CreatedByUser != null ? x.CreatedByUser["FirstName"] + " " + x.CreatedByUser["LastName"] : "";
        });
        this.defaultImage = Constants.DefaultImagePaths.Material; // Reset image to default
        this.attachments.total = response.Attachments.length;
        let defImage = response.Attachments.find(x => x.IsDefaultImage == true);
        this.defaultImage = !CommonMethods.CheckNullOrUndefined(defImage) ? defImage.FilePath : this.defaultImage;
        if (!CommonMethods.CheckNullOrUndefined(this.details.Category.FullName)) {
          this.details.Category.FullName = this.details.Category.FullName.replace(/\//g, ':');
        }
        if (!CommonMethods.CheckNullOrUndefined(response)) {
          this.details.PriceBookItem = {
            IsFrequentlyUsed: response.IsFrequentlyUsed,
            IsTaxable: response.IsTaxable,
            IsDescriptionEditAllowed: response.IsDescriptionEditAllowed,
            IsPriceEditAllowed: response.IsPriceEditAllowed,
            IsAgreementExcluded: response.IsAgreementExcluded
          };
        }
      }
      this.showspinner.hideLoader(this.viewContainerRef, this.tabCounter);
    }, error => {
      this.showspinner.hideLoader(this.viewContainerRef, this.tabCounter);
    });
  }
  private checkIsNotImage(dataItem) {
    if (dataItem != null) {
      let fileName = dataItem.FilePath;
      if (!CommonMethods.CheckNullOrUndefined(dataItem["ExternalAttachment"])) {
        fileName = dataItem["ExternalAttachment"]["AttachmentPath"];
      }
      if (fileName != "") {
        let exts = ['.jpg', '.jpeg', '.gif', '.png', '.bmp'];
        if (!this.commonMethods.ValidExtension(fileName, exts))
          return true;
        else
          return false;
      }
      else {
        if (!CommonMethods.CheckNullOrUndefined(dataItem.FilePath) && dataItem.FilePath.indexOf("https://mpsuitedevstore.blob.core.windows.net") < 0) {
          return true;
        }
      }
    }
    else
      return false;
  }
  //#endregion 
}
